import { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingDown, TrendingUp, Bell, BellOff, ChevronDown, DollarSign, AlertCircle, Trash2, ExternalLink, Store, ShoppingCart, Camera, Cpu } from 'lucide-react';
import { getAffiliateLinks } from '@/data/affiliateLinks';
import { gpuPricing } from '@/data/vendorPrices';
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const retailerConfig: Record<string, { icon: React.ReactNode; bgColor: string; hoverBgColor: string; textColor: string }> = {
  'Amazon': { icon: <ShoppingCart className="w-3 h-3" />, bgColor: 'bg-orange-100', hoverBgColor: 'hover:bg-orange-200', textColor: 'text-orange-700' },
  'Newegg': { icon: <Cpu className="w-3 h-3" />, bgColor: 'bg-amber-100', hoverBgColor: 'hover:bg-amber-200', textColor: 'text-amber-700' },
  'Best Buy': { icon: <Store className="w-3 h-3" />, bgColor: 'bg-blue-100', hoverBgColor: 'hover:bg-blue-200', textColor: 'text-blue-700' },
  'B&H Photo': { icon: <Camera className="w-3 h-3" />, bgColor: 'bg-slate-100', hoverBgColor: 'hover:bg-slate-200', textColor: 'text-slate-700' },
};

const defaultRetailerConfig = { icon: <ExternalLink className="w-3 h-3" />, bgColor: 'bg-muted/50', hoverBgColor: 'hover:bg-muted', textColor: 'text-foreground' };
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { gpuData, GPU } from '@/data/gpuData';
import { gpuPriceData, getPriceHistoryForGPU, getPriceChange } from '@/data/priceData';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { usePriceAlerts } from '@/hooks/usePriceAlerts';

const brandColors: Record<string, string> = {
  nvidia: 'hsl(83, 100%, 42%)',
  amd: 'hsl(0, 80%, 55%)',
  intel: 'hsl(210, 100%, 50%)',
};

export const PriceTracker = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { alerts, createAlert, deleteAlert, getAlertForGPU } = usePriceAlerts();
  
  const [selectedGPU, setSelectedGPU] = useState<GPU>(gpuData[0]);
  const [watchedGPUs, setWatchedGPUs] = useState<Set<string>>(new Set());
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [alertPrice, setAlertPrice] = useState('');

  const priceHistory = useMemo(() => getPriceHistoryForGPU(selectedGPU.id), [selectedGPU]);
  const priceChange = priceHistory ? getPriceChange(priceHistory) : { change: 0, percentage: 0 };
  const existingAlert = getAlertForGPU(selectedGPU.id);

  const toggleWatch = (gpuId: string) => {
    setWatchedGPUs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(gpuId)) {
        newSet.delete(gpuId);
        toast({
          title: "Removed from watchlist",
          description: `You'll no longer receive alerts for ${gpuData.find(g => g.id === gpuId)?.name}`,
        });
      } else {
        newSet.add(gpuId);
        toast({
          title: "Added to watchlist",
          description: `You'll be notified when ${gpuData.find(g => g.id === gpuId)?.name} drops in price`,
        });
      }
      return newSet;
    });
  };

  const handleSetAlert = async () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to set price alerts",
        variant: "destructive",
      });
      return;
    }

    if (!alertPrice) {
      toast({
        title: "Missing information",
        description: "Please enter a target price",
        variant: "destructive",
      });
      return;
    }

    const { error } = await createAlert(selectedGPU.id, parseFloat(alertPrice));
    
    if (error) {
      toast({
        title: "Error creating alert",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Price alert set!",
      description: `We'll notify you when ${selectedGPU.name} drops to $${alertPrice}`,
    });
    setAlertDialogOpen(false);
    setAlertPrice('');
  };

  const handleDeleteAlert = async (alertId: string) => {
    const { error } = await deleteAlert(alertId);
    
    if (!error) {
      toast({
        title: "Alert removed",
        description: "Price alert has been deleted",
      });
    }
  };

  // Get top price drops
  const topPriceDrops = useMemo(() => {
    return gpuPriceData
      .map(p => {
        const gpu = gpuData.find(g => g.id === p.gpuId);
        const change = getPriceChange(p);
        return { ...p, gpu, change };
      })
      .filter(p => p.gpu && p.change.percentage < 0)
      .sort((a, b) => a.change.percentage - b.change.percentage)
      .slice(0, 5);
  }, []);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border shadow-lg rounded-lg p-3">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-bold text-foreground">${payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <section id="prices" className="py-16 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
              <DollarSign className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">Price Tracker</span>
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              GPU Price History & Alerts
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Track historical prices and get notified when your favorite GPUs drop in price.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Price Chart */}
            <div className="lg:col-span-2">
              <Card className="p-6">
                {/* GPU Selector */}
                <div className="flex items-center justify-between mb-6">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" className="gap-2 bg-card">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: brandColors[selectedGPU.brand] }}
                        />
                        <span>{selectedGPU.name}</span>
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="max-h-[300px] overflow-y-auto bg-card border shadow-lg">
                      {gpuData.map((gpu) => (
                        <DropdownMenuItem
                          key={gpu.id}
                          onClick={() => setSelectedGPU(gpu)}
                          className="cursor-pointer"
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full mr-2"
                            style={{ backgroundColor: brandColors[gpu.brand] }}
                          />
                          {gpu.name}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="flex items-center gap-2">
                    <Button
                      variant={watchedGPUs.has(selectedGPU.id) ? "default" : "outline"}
                      size="sm"
                      onClick={() => toggleWatch(selectedGPU.id)}
                      className="gap-2"
                    >
                      {watchedGPUs.has(selectedGPU.id) ? (
                        <>
                          <Bell className="w-4 h-4" />
                          Watching
                        </>
                      ) : (
                        <>
                          <BellOff className="w-4 h-4" />
                          Watch
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAlertDialogOpen(true)}
                    >
                      {existingAlert ? 'Update Alert' : 'Set Alert'}
                    </Button>
                  </div>
                </div>

                {/* Existing Alert Banner */}
                {existingAlert && (
                  <div className="flex items-center justify-between p-3 mb-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-primary" />
                      <span className="text-sm">
                        Alert set for <strong>${existingAlert.target_price}</strong>
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAlert(existingAlert.id)}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      aria-label="Delete price alert"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Price Stats */}
                {priceHistory && (
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-xs text-muted-foreground mb-1">Current</div>
                      <div className="text-xl font-bold">${priceHistory.currentPrice}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-xs text-muted-foreground mb-1">MSRP</div>
                      <div className="text-xl font-bold">${priceHistory.msrp}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-xs text-muted-foreground mb-1">Lowest</div>
                      <div className="text-xl font-bold text-green-600">${priceHistory.lowestPrice}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <div className="text-xs text-muted-foreground mb-1">6mo Change</div>
                      <div className={cn(
                        'text-xl font-bold flex items-center gap-1',
                        priceChange.percentage < 0 ? 'text-green-600' : 'text-red-500'
                      )}>
                        {priceChange.percentage < 0 ? (
                          <TrendingDown className="w-4 h-4" />
                        ) : (
                          <TrendingUp className="w-4 h-4" />
                        )}
                        {priceChange.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                )}

                {/* Chart */}
                {priceHistory && (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={priceHistory.priceHistory} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="date"
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getMonth() + 1}/${date.getDate()}`;
                          }}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          domain={['dataMin - 50', 'dataMax + 50']}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          tickFormatter={(value) => `$${value}`}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <ReferenceLine
                          y={priceHistory.msrp}
                          stroke="hsl(var(--primary))"
                          strokeDasharray="5 5"
                          label={{ value: 'MSRP', fill: 'hsl(var(--primary))', fontSize: 10 }}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke={brandColors[selectedGPU.brand]}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 6, fill: brandColors[selectedGPU.brand] }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Top Price Drops */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-green-600" />
                  Biggest Price Drops
                </h3>
                <div className="space-y-3">
                  {topPriceDrops.map((item) => {
                    const affiliateLinks = getAffiliateLinks(item.gpuId);
                    return (
                      <div
                        key={item.gpuId}
                        className="p-3 rounded-lg border hover:border-primary/50 transition-colors"
                      >
                        <button
                          onClick={() => item.gpu && setSelectedGPU(item.gpu)}
                          className="w-full text-left"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{item.gpu?.name}</span>
                            <Badge variant="secondary" className="text-green-600 bg-green-100">
                              {item.change.percentage.toFixed(1)}%
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Now ${item.currentPrice} (was ${item.priceHistory[0].price})
                          </div>
                        </button>
                        <TooltipProvider>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {affiliateLinks.map((link) => {
                              const config = retailerConfig[link.retailer] || defaultRetailerConfig;
                              const vendorData = gpuPricing[item.gpuId]?.vendors.find(v => v.retailer === link.retailer);
                              const stockStatus = vendorData?.inStock ? 'In Stock' : 'Out of Stock';
                              const priceText = vendorData?.price ? `$${vendorData.price}` : 'Price unavailable';
                              
                              return (
                                <UITooltip key={link.retailer}>
                                  <TooltipTrigger asChild>
                                    <a
                                      href={link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${config.bgColor} ${config.hoverBgColor} ${config.textColor}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {config.icon}
                                      {link.retailer}
                                    </a>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <div className="font-medium">{priceText}</div>
                                      <div className={vendorData?.inStock ? 'text-green-500' : 'text-red-400'}>
                                        {stockStatus}
                                      </div>
                                    </div>
                                  </TooltipContent>
                                </UITooltip>
                              );
                            })}
                          </div>
                        </TooltipProvider>
                      </div>
                    );
                  })}
                </div>
              </Card>

              {/* Your Price Alerts */}
              <Card className="p-4">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-primary" />
                  Your Price Alerts
                </h3>
                {!user ? (
                  <div className="text-center py-6 text-muted-foreground text-sm">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Sign in to set price alerts</p>
                    <Button asChild size="sm" className="mt-3">
                      <a href="/auth">Sign In</a>
                    </Button>
                  </div>
                ) : alerts.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground text-sm">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No price alerts set</p>
                    <p className="text-xs mt-1">Click "Set Alert" on a GPU to track its price</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alerts.map((alert) => {
                      const gpu = gpuData.find(g => g.id === alert.gpu_id);
                      if (!gpu) return null;
                      return (
                        <div
                          key={alert.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: brandColors[gpu.brand] }}
                            />
                            <div className="min-w-0">
                              <span className="text-sm font-medium block truncate">{gpu.name}</span>
                              <span className="text-xs text-muted-foreground">Target: ${alert.target_price}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteAlert(alert.id)}
                            className="text-muted-foreground hover:text-destructive ml-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* Account CTA for non-logged in users */}
              {!user && (
                <Card className="p-4 bg-primary/5 border-primary/20">
                  <h3 className="font-semibold mb-2">Get Price Alerts</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Create an account to receive email notifications when GPUs drop in price.
                  </p>
                  <Button asChild size="sm" className="w-full">
                    <a href="/auth">Create Account</a>
                  </Button>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Price Alert Dialog */}
      <Dialog open={alertDialogOpen} onOpenChange={setAlertDialogOpen}>
        <DialogContent className="bg-card">
          <DialogHeader>
            <DialogTitle>Set Price Alert for {selectedGPU.name}</DialogTitle>
            <DialogDescription>
              {user 
                ? "We'll notify you when this GPU drops to your target price."
                : "Sign in to set price alerts and receive notifications."}
            </DialogDescription>
          </DialogHeader>
          {user ? (
            <div className="space-y-4 mt-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Target Price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    placeholder={priceHistory?.lowestPrice.toString() || "500"}
                    value={alertPrice}
                    onChange={(e) => setAlertPrice(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {priceHistory && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Current: ${priceHistory.currentPrice} • Lowest: ${priceHistory.lowestPrice}
                  </p>
                )}
              </div>
              <Button onClick={handleSetAlert} className="w-full">
                Set Alert
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <Button asChild className="w-full">
                <a href="/auth">Sign In to Set Alerts</a>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
